import { Availability } from '@/types/cart';
import { Address } from '@/types/user';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

interface CartState {
  cartId: number;
  selectedCatalogNumbers: string[];
  address: CartSelectAddress;
  availabilityByCatalogNumber?: {
    [key: string]: Availability;
  };
  isFreightVatError: boolean;
  changeSelectedProducts: (catalogNumber: string | string[]) => void;
  changeCartId: (cartId: number) => void;
  changeAddress: (address: CartSelectAddress) => void;
  setSelectedCatalogNumbers: (selectedCatalogNumbers: string[]) => void;
  addAvailabilityByCatalogNumber: (availability: Availability, catalogNumber: string) => void;
  removeAvailabilityByCatalogNumber: (catalogNumber: string) => void;
  clearAvailabilityByCatalogNumber: () => void;
  setIsFreightVatError: (isFreightVatError: boolean) => void;
}

export type CartSelectAddress = Pick<Address, 'addressKey' | 'accountNumber' | 'provinceCode' | 'categoryCode03'> &
  Record<'isActive', boolean>;

export const useCartStore = create<CartState>()(
  devtools(
    immer((set, get) => ({
      availabilityByCatalogNumber: {},
      selectedCatalogNumbers: [],
      cartId: 0,
      address: null,
      isFreightVatError: false,
      setIsFreightVatError: (isFreightVatError) => {
        set((state) => {
          state.isFreightVatError = isFreightVatError;
        });
      },
      changeCartId: (cartId) => {
        set((state) => {
          state.cartId = cartId;
        });
      },
      changeSelectedProducts: (catalogNumber) => {
        if (Array.isArray(catalogNumber)) {
          set({ selectedCatalogNumbers: catalogNumber });
        } else {
          const oldSelectedCatalogNumbers = useCartStore.getState().selectedCatalogNumbers ?? [];
          const newSelectedCatalogNumbers = [...oldSelectedCatalogNumbers];
          const index = newSelectedCatalogNumbers.findIndex((no) => no === catalogNumber);
          if (index > -1) {
            newSelectedCatalogNumbers.splice(index, 1);
          } else {
            newSelectedCatalogNumbers.push(catalogNumber);
          }
          set((state) => {
            state.selectedCatalogNumbers = newSelectedCatalogNumbers;
          });
        }
      },
      changeAddress: (address) => {
        set({
          address,
        });
      },
      setSelectedCatalogNumbers: (selectedCatalogNumbers: string[]) => set({ selectedCatalogNumbers }),
      addAvailabilityByCatalogNumber: (availability, catalogNumber) => {
        set((state) => {
          state.availabilityByCatalogNumber[catalogNumber] = availability;
        });
      },
      removeAvailabilityByCatalogNumber: (catalogNumber) => {
        set((state) => {
          delete state.availabilityByCatalogNumber[catalogNumber];
        });
      },
      clearAvailabilityByCatalogNumber: () => {
        set((state) => {
          state.availabilityByCatalogNumber = {};
        });
      },
    })),
    {
      name: 'cart-store',
    }
  )
);
